import { m, type Message } from '@/i18n';
import type { Verdict } from './constants';
import { lifecycleReading } from './tags';

/**
 * The state-chip law (settled 2026-07-25, docs/CLOSED_AXIS.md "Quiet defaults"):
 * which of the item's axes SPEAK on the status line, and with which word.
 *
 * The three atoms stay three atoms - this is a display law over them, never a
 * model change and never a stored word (the standing retirement holds: no
 * bundled platform vocabulary, no `open·active·closed` tri-state). What changed
 * is that an axis at REST no longer spends a pixel saying so.
 *
 * The rules, in the order they were settled:
 *
 * - **R1, rest is silent.** idle / undelivered / open / no verdict render
 *   nothing. A fresh item carries zero chips (today: four controls saying
 *   nothing has happened). **One exception since 2026-09-16** (the state
 *   line): a surface that makes the chips EDITABLE passes `resting`, and the
 *   lifecycle axis then wears `Idle` at rest, because that chip is the axis's
 *   only home there - see `stateChips`'s own note. Every summary row leaves
 *   the option off and stays silent.
 * - **R2, the verdict is a record of the review** (Leon, 2026-11-03,
 *   revisiting the rejection of 2026-07-25). Finishing is one lifecycle with
 *   four positions, and the verdict is written only by the review's own edges
 *   (Accept, Deny, Reopen), so `Accepted` implies `Done`: a Done item wears
 *   `Done` plus `Accepted`, and an item a Deny sent back is open with
 *   `Denied` until the next Ready for review or Done clears it.
 * - **R3, dominance.** `closed` suppresses `active` - the atom persists so
 *   Reopen restores it. The position chip speaks the READING
 *   (`lifecycleReading`): `Review` while a delivery stands open, `Done` or
 *   `Cancelled` once closed, nothing while open (Leon, 2026-11-03: one word
 *   per ending). A `Denied` record is silent on a concluded item.
 * - **R4, the verdict speaks only once it HAS one.** `Accepted` / `Denied`
 *   are records of the review edges, never an axis of their own; nothing is
 *   said while no verdict stands.
 * - **R5, health is its own voice** and is not computed here (it renders in
 *   every state, closed included - the anti-gaming clause).
 * - **R6, chips are item-truth, viewer-independent.** Only the ACTS behind them
 *   vary by role, which is why this function takes no viewer. A shared
 *   vocabulary that changed per reader would not be one.
 *
 * The positions fall out rather than being stored: delivered and open IS
 * review, delivered and closed IS done, closed without a delivery IS a
 * cancellation (`lifecycleReading`, domain/tags.ts).
 */

/** The item-carried axes the chips read. Seats are deliberately NOT here
 *  (2026-09-16): the last thing that asked who was owed a look was the
 *  retired `Awaiting review` word, and R6 says the chips are item-truth. */
export interface StateChipSnapshot {
  delivered: boolean;
  active: boolean;
  closed: boolean;
  verdict: Verdict;
}

/** The chips that can speak, in the order `stateChips` emits them: the
 *  activity, the position (`delivered` speaks `Review`, `closed` speaks `Done`
 *  or `Cancelled`; one of the two at most), then the verdict's record. */
export const STATE_CHIP_KINDS = ['active', 'delivered', 'closed', 'verdict'] as const;
export type StateChipKind = (typeof STATE_CHIP_KINDS)[number];

export interface StateChip {
  kind: StateChipKind;
  /** An axis name, rendered dim ahead of the word. No chip wears one since
   *  2026-11-03: the verdict's words are records of the review edges and
   *  name themselves beside the position. */
  axis?: Message;
  /** The word, as a descriptor: the surface formats it in the viewer's locale
   *  (docs/I18N.md §6), so the domain never holds a sentence. */
  label: Message;
  /** Colour voice. Chips are DOTLESS whatever the tone - the dot is health's
   *  alone (HEALTH_SIGNALS §5); a tone reaches the text and border only. */
  tone: 'plain' | 'good' | 'alarm';
}

/** How the run is rendered. */
export interface StateChipOptions {
  /**
   * Give the LIFECYCLE axis a rest word (`Idle`) instead of silence - the one
   * bend in R1, and only for the item page's state line (Leon, 2026-09-16).
   *
   * There the chips are the axis's EDITING home: tapping `Idle` sets Active,
   * tapping `Active` sets Idle. An axis with no chip has nothing to tap, so
   * the one axis that is always editable is the one axis that always speaks.
   * A summary row has nothing to edit, leaves the option off, and keeps R1
   * whole. A flow MEMBER gets it too since 2026-09-21 (Leon: every attribute
   * needed to move an item is on the line); the stage word leads the line and
   * answers a different question.
   *
   * Closed still suppresses it (R3): a concluded item is not resting.
   */
  resting?: boolean;
}

/**
 * The chips an item's status line renders: the activity, the position, then
 * the verdict's record. Empty for an untouched item, unless `resting` asks for the lifecycle
 * axis's rest word.
 */
export function stateChips(s: StateChipSnapshot, opts: StateChipOptions = {}): StateChip[] {
  const chips: StateChip[] = [];

  // R3: the engagement declaration persists through a close (Reopen restores
  // it) but reads inactive while concluded, so the chip goes quiet.
  if (s.active && !s.closed) {
    // Green (Leon, 2026-07-26): being carried is a POSITIVE state, and the
    // tone vocabulary already exists for exactly this (`accepted` wears it).
    // Still DOTLESS - the dot is health's alone (HS §5), so a green word here
    // cannot be mistaken for, or used to paint, a health verdict; the
    // divergence facts keep firing against a declared-active item regardless
    // (MACHINE.md §4: declaring active buys no invisibility).
    chips.push({ kind: 'active', label: m('itemActions.stateChip.active'), tone: 'good' });
  } else if (opts.resting && !s.closed) {
    // The rest word, plain: nothing has been taken up, and the chip is the tap
    // target that takes it up.
    chips.push({ kind: 'active', label: m('itemActions.stateChip.idle'), tone: 'plain' });
  }

  // The POSITION (Leon, 2026-11-03): the reading over the two atoms, one word
  // per ending. Plain whatever the position: the destructive ACT is
  // signalled, the resting state is not (colour marks what needs attention).
  // The close reason stays off the chip; it rides the `concluded` event.
  const position = lifecycleReading(s);
  if (position === 'review') {
    chips.push({ kind: 'delivered', label: m('itemActions.stateChip.review'), tone: 'plain' });
  } else if (position !== 'open') {
    chips.push({
      kind: 'closed',
      label:
        position === 'cancelled'
          ? m('itemActions.stateChip.cancelled')
          : m('itemActions.stateChip.done'),
      tone: 'plain',
    });
  }

  // R2 (2026-11-03): the verdict is the record of the review. `Accepted`
  // stands beside `Done`; `Denied` stands while the item is not concluded (a
  // Deny sends it back to open, and the next delivery clears the record).
  if (s.verdict === 'accepted') {
    chips.push({
      kind: 'verdict',
      label: m('item.state.verdict.accepted'),
      tone: 'good',
    });
  } else if (s.verdict === 'disputed' && !s.closed) {
    // The stored key stays `disputed`; the product word is Denied (2026-07-18).
    chips.push({
      kind: 'verdict',
      label: m('item.state.verdict.denied'),
      tone: 'alarm',
    });
  }

  return chips;
}

/** Where the state column ranks a row: how FAR it has moved (its position, in
 *  the order open, review, done, cancelled, with an active open item past an
 *  idle one), then how many chips speak. A row wearing nothing ranks 0 - the
 *  start of the order, not an absent value. The list's `state` column sorts
 *  by it (src/app/boards/list-columns.ts), and the listing statement orders
 *  by the same rank, read over every snapshot (src/read/listing-criteria.ts). */
const POSITION_RANK = { open: 0, review: 2, done: 3, cancelled: 4 } as const;

export function stateRankOf(s: StateChipSnapshot): number {
  const chips = stateChips(s);
  if (chips.length === 0) return 0;
  const position = lifecycleReading(s);
  const band = position === 'open' && s.active ? 1 : POSITION_RANK[position];
  // At most three chips ride a row, so the count never crosses into the next
  // band.
  return band * 4 + chips.length;
}

/* `nextAct` / `NEXT_ACT_VERB` / `NextActKind` are RETIRED here (2026-09-21).
 * The ladder they served - one linear next step, read off the atoms - is the
 * act sheet's job now, and the act sheet asks a wider question (the seats, a
 * member's flow, which acts are locked): `src/domain/act-sheet.ts` owns the
 * verbs and their order, this file owns which VALUES speak on the line. The
 * words are unchanged - Start, Deliver, Close - they just live with the acts
 * that carry them. */
